/**
 * Parts / hotspots overlay — the connected "Details" mode.
 *
 * ONE product `hotspots[]` array (capped to 4 by the caller) drives a tappable dot
 * per part AND a camera focus AND a menu row. Each dot is anchored to a NORMALISED
 * point inside the live bounding box (0..1 per axis) so a region like "left
 * armrest" stays correctly placed across shape/size switches.
 *
 * Tapping a dot (or focusPart from the menu) focuses the camera and opens the
 * MODAL (a solid black detail card with the part's info + a "Reset view" button).
 * Reset view is the full exit: camera resets, dots disappear, and onExit fires so
 * the menu returns to its main pane.
 *
 * Mirrors DimensionsOverlay's projection spine: dots are DOM, repositioned on
 * every camera change via engine.projectToScreen().
 */

import type { Engine } from "@/engine/Engine";
import type { HotspotDef } from "@/types";

export interface PartsOverlayApi {
  /** Show/hide the dots (Details mode on/off). */
  setActive(on: boolean): void;
  isActive(): boolean;
  /** Focus a part by id — opens the modal. Used by the menu rows + dot taps. */
  focusPart(id: string): void;
  /** Full exit: reset the camera, hide the dots + modal, and fire onExit. */
  resetView(): void;
  /** Hide the detail card but KEEP the camera focus + dots (tap model / open menu). */
  dismissCard(): void;
  /** Recompute dot positions (call after a rebuild). */
  refresh(): void;
  destroy(): void;
}

interface PartDot {
  part: HotspotDef;
  dot: HTMLButtonElement;
}

export function createPartsOverlay(
  engine: Engine,
  viewer: HTMLElement,
  parts: HotspotDef[],
  opts: { onExit?: () => void } = {},
): PartsOverlayApi {
  let active = false;

  const dots: PartDot[] = [];
  for (const part of parts) {
    const dot = document.createElement("button");
    dot.type = "button";
    dot.className = "mob-hotspot";
    dot.dataset.partId = part.id;
    dot.setAttribute("aria-label", part.label);
    dot.style.display = "none";
    const ring = document.createElement("span");
    ring.className = "mob-hotspot__ring";
    dot.appendChild(ring);
    dot.addEventListener("click", (e) => {
      e.stopPropagation();
      focus(part.id);
    });
    viewer.appendChild(dot);
    dots.push({ part, dot });
  }

  // The MODAL: a solid black detail card (part label + detail + Reset view).
  const card = document.createElement("div");
  card.className = "mob-part-detail";
  card.style.display = "none";
  const cardTitle = document.createElement("div");
  cardTitle.className = "mob-part-detail__title";
  const cardBody = document.createElement("div");
  cardBody.className = "mob-part-detail__body";
  const cardReset = document.createElement("button");
  cardReset.type = "button";
  cardReset.className = "mob-part-detail__reset";
  cardReset.textContent = "Reset view";
  cardReset.addEventListener("click", (e) => {
    e.stopPropagation();
    fullExit();
  });
  card.append(cardTitle, cardBody, cardReset);
  viewer.appendChild(card);

  /** Reposition every dot for the current camera (hide ones behind the camera). */
  function redraw(): void {
    if (!active) return;
    for (const { part, dot } of dots) {
      const world = engine.anchorToWorld(part.anchor);
      if (!world) {
        dot.style.display = "none";
        continue;
      }
      const p = engine.projectToScreen(world);
      if (p.behind) {
        dot.style.display = "none";
        continue;
      }
      dot.style.display = "";
      dot.style.left = `${p.x}px`;
      dot.style.top = `${p.y}px`;
    }
  }

  /** Focus a part: glide the camera and open the modal. */
  function focus(id: string): void {
    const entry = dots.find((d) => d.part.id === id);
    if (!entry) return;
    engine.focusOnPart(entry.part);
    for (const { part, dot } of dots) dot.classList.toggle("is-active", part.id === id);
    cardTitle.textContent = entry.part.label;
    cardBody.textContent = entry.part.detail ?? "";
    cardBody.style.display = entry.part.detail ? "" : "none";
    card.style.display = "";
  }

  /** Camera back to default + clear the focus highlight + close the modal. Dots
   *  STAY (used on a rebuild while the user is still in Details). */
  function resetFocus(): void {
    engine.resetView();
    for (const { dot } of dots) dot.classList.remove("is-active");
    card.style.display = "none";
  }

  /** Hide the dots + modal and drop the active flag (no camera change). */
  function deactivate(): void {
    active = false;
    for (const { dot } of dots) {
      dot.style.display = "none";
      dot.classList.remove("is-active");
    }
    card.style.display = "none";
  }

  /** Full exit (modal "Reset view" / menu back): camera reset, dots gone, notify. */
  function fullExit(): void {
    engine.resetView();
    deactivate();
    opts.onExit?.();
  }

  // Redraw on camera move (orbit/zoom/focus glide). On rebuild, drop the focus but
  // stay in Details and reposition the dots for the new geometry.
  const offCam = engine.onCameraChange(() => redraw());
  engine.onAssemblyShown(() => {
    if (active) {
      resetFocus();
      redraw();
    }
  });

  /** Hide the detail card but KEEP the camera focus + the dots (tap the model body
   *  with the card open, or open the menu). The window reappears on the next dot tap. */
  function dismissCard(): void {
    card.style.display = "none";
    for (const { dot } of dots) dot.classList.remove("is-active");
  }

  // Tapping the MODEL / empty area (a tap, not a drag, and not on a dot or the card)
  // dismisses the card while keeping the camera where it is. A drag = rotate → keep.
  let downX = 0;
  let downY = 0;
  const onViewerDown = (e: PointerEvent) => { downX = e.clientX; downY = e.clientY; };
  const onViewerUp = (e: PointerEvent) => {
    if (!active || card.style.display === "none") return;
    if (Math.hypot(e.clientX - downX, e.clientY - downY) >= 8) return; // drag → keep card
    const t = e.target as HTMLElement | null;
    if (t && (t.closest(".mob-hotspot") || t.closest(".mob-part-detail"))) return; // dot/card tap
    dismissCard();
  };
  viewer.addEventListener("pointerdown", onViewerDown);
  viewer.addEventListener("pointerup", onViewerUp);

  return {
    setActive(on: boolean): void {
      if (on) {
        active = true;
        for (const { dot } of dots) dot.style.display = "";
        redraw();
      } else {
        deactivate();
      }
    },
    isActive: () => active,
    focusPart(id: string): void {
      if (active) focus(id);
    },
    resetView: fullExit,
    dismissCard,
    refresh(): void {
      if (active) redraw();
    },
    destroy(): void {
      offCam();
      viewer.removeEventListener("pointerdown", onViewerDown);
      viewer.removeEventListener("pointerup", onViewerUp);
      for (const { dot } of dots) dot.remove();
      dots.length = 0;
      card.remove();
    },
  };
}
